import React, { useState, useEffect, useMemo } from 'react';
import { inject, observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';

import Typography from '@material-ui/core/Typography';
import Container from '@material-ui/core/Container';
import GetAppIcon from '@material-ui/icons/GetApp';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';

import { Permission } from 'helpers/permissions';
import { chart, chartTooltipData } from 'helpers/chart';
import Background from 'components/Layout/Background';
import LoadingIndicator from 'components/Loading/LoadingIndicator';
import ControlButtons from 'components/ControlButtons';
import CustomPaper from 'components/Layout/Background';

import { RootStore } from 'stores/rootStore';
import { ApiStore } from 'stores/apiStore';
import { OverviewStore } from 'stores/account/overviewStore/overviewStore';
import Chart from 'pages/Accounts/Edit/Plan/Chart/Chart';
import Composition from 'pages/Accounts/Edit/Plan/Composition';
import { ViewMode } from 'pages/Accounts/Edit/components/viewModes';
import Deposits from 'pages/Accounts/Edit/components/Deposits';
import NewReserves from 'pages/Accounts/Edit/components/Reserves';
import AdditionalTargets from 'pages/Accounts/Edit/components/AdditionalTargets';
import { Flows } from 'pages/Accounts/Detail/enums';
import { Currency } from 'enums';
import useStyles from './overviewStyles';
import BucketTabSection from 'components/Bucket/BucketTabSection';
import PortfolioTableComparison from 'components/PortfolioTables/PortfolioTableComparison';
import GeneralOverview from './GeneralOverview';
import ExternalProducts from 'pages/Accounts/Edit/components/ExternalProducts/ExternalProducts';
import { AccountDetailStore } from 'stores/account/accountDetailStore';
import { API_ACCOUNT_DOWNLOAD_OVERVIEW } from 'api/endpoints';

interface Props {
  rootStore?: RootStore;
  apiStore?: ApiStore;
  accountDetailStore: AccountDetailStore;
}

export enum RebalancingAction {
  'NONE' = 'NONE',
  'AUTO' = 'AUTO',
  'ONCE' = 'ONCE',
  'AUTO_ONCE' = 'AUTO_ONCE',
  'AUTO_STRICT' = 'AUTO_STRICT',
}

const Overview: React.FC<Props> = ({ rootStore, apiStore, accountDetailStore }) => {
  const { currency, accountId } = accountDetailStore;
  const flow = accountDetailStore.flow!.id;
  const [store] = useState(new OverviewStore(rootStore as RootStore, accountId, flow));
  const [pdfDownloading, setPdfDownloading] = useState(false);
  const { t } = useTranslation();
  const classes = useStyles();
  const currentAccountTitle = accountDetailStore.data.detail?.title || '';
  const currentAccountBalance = accountDetailStore.data.detail?.balance || 0;


  useEffect(() => {
    (async () => {
      await store.fetchOverview();
      const top = document.querySelector('.MuiStepper-root');
      top && top.scrollIntoView();
    })();
  }, [store]);

  const downloadOverview = useMemo(
    () => async () => {
      setPdfDownloading(true);
      await apiStore?.get(API_ACCOUNT_DOWNLOAD_OVERVIEW(accountId));
      setPdfDownloading(false);
    },
    [accountId, apiStore],
  );

  const currencyHedgingEnabled =
    rootStore!.stores.userStore.checkPermissions(Permission.ACCOUNT_HEDGING) && currency === Currency.CZK;
  const rebalancingEnabled = rootStore!.stores.userStore.checkPermissions(Permission.ACCOUNT_REBALANCING);

  return (
    <Container maxWidth="md">
      <Background>
        {store.data ? (
          <>
            <GeneralOverview
              data={store.data}
              flow={flow}
              currency={currency}
              transfers={store.transfers}
              transfersFromCashOnly={store.transfersFromCashOnly}
              withdrawAll={store.withdrawAll}
            />

            <Typography variant="h2" className={classes.proposedTitle}>
              {t('accountDetail_overview_proposedTitle')}
            </Typography>
            <div className={classes.wrapper}>
              {store.proposedChartData.inflation !== undefined && flow !== Flows.WITHDRAWAL && (
                <Chart
                  planData={store.data.proposed}
                  data={chart(store.data.proposed, false, store.data.proposed.externalProductData?.externalProducts)}
                  metaData={store.proposedChartData}
                  chartTooltipData={chartTooltipData(
                    store.data.proposed,
                    false,
                    store.data.proposed.externalProductData?.externalProducts,
                  )}
                  currency={currency}
                  stacking="normal"
                  accountId={accountId}
                  store={store}
                  readOnly={true}
                  overview
                  userMode={false}
                />
              )}
              {store.data.proposed.externalProductData.externalProducts?.length !== 0 && ( //EDS-4891
                <>
                  <CustomPaper padding={4} style={{ marginTop: '8px', padding: '32px 0' }} inside>
                    <Composition
                      extProdData={store.data.proposed.externalProductData.externalProducts}
                      composition={store.data.proposed.chart.composition}
                      finInst={store.data.proposed.chart.metadata.financialInstitutions}
                      overview
                    />
                  </CustomPaper>
                  <Typography variant="h3" className={classes.proposedSubtitle}>
                    {t('finQuestionnary_goalPlan_resources')}
                  </Typography>
                  <ExternalProducts
                    accountId={accountId}
                    productsData={store.data.proposed.externalProductData}
                    currency={currency}
                    overview
                    currentAccount={{
                      title: currentAccountTitle,
                      balance: currentAccountBalance,
                      profit: store.data.proposed.buckets[2].portfolio.income,
                    }}
                    viewMode={ViewMode.DETAIL}
                  />
                </>
              )}

              <Typography variant="h3" className={classes.proposedSubtitle}>
                {t('deposits_plannedDeposits')}
              </Typography>
              <Deposits
                accountId={accountId}
                currency={currency}
                readOnly
                viewMode={ViewMode.DETAIL}
                data={store.data.proposed.depositData}
                planStore={store}
                mode={store.data.proposed.mode}
                handleDepositModal={() => {}}
                userMode={false}
                overview
              />

              {store.data.proposed.reserveData?.reserves.length > 0 && (
                <>
                  <Typography variant="h3" className={classes.proposedSubtitle}>
                    {t('accountPlan_reserves')}
                  </Typography>
                  <NewReserves
                    readOnly={true}
                    userMode={false}
                    viewMode={ViewMode.DETAIL}
                    currency={currency}
                    reservesData={store.data.proposed.reserveData}
                    accountId={accountId}
                    fullWidth={true}
                    collapsed={false}
                    plan={false}
                    planStore={store}
                    overview
                  />
                </>
              )}

              {(store.data.proposed.targetData?.targets.length > 0 || store.data.proposed.rentData) && (
                <>
                  <Typography variant="h3" className={classes.proposedSubtitle}>
                    {t('finQuestionnary_target_otherFinancialTargets')}
                  </Typography>
                  <AdditionalTargets
                    accountId={accountId}
                    rentData={store.data.proposed.rentData}
                    targetsData={store.data.proposed.targetData.options ? store.data.proposed.targetData : undefined}
                    targetsOnly={!store.data.proposed.rentApplicable}
                    plan={false}
                    currency={currency}
                    viewMode={ViewMode.DETAIL}
                    readOnly={true}
                    userMode={false}
                    collapsed={store.data.proposed.portfolioOnly || store.proposedChartData.showRange}
                    personType={store.data.proposed.personType}
                    planStore={store}
                    defaultInflation={0}
                    defaultInflationType=""
                    overview
                  />
                </>
              )}
            </div>

            <div className={classes.wrapper}>
              <Typography variant="h3" className={classes.proposedSubtitle}>
                {t('accountDetail_overview_bucketsTitle')}
              </Typography>
              <br />
              <BucketTabSection
                buckets={store.data.proposed.buckets.slice().reverse()}
                originalBuckets={store.data.original.buckets.slice().reverse()}
                activatedBucketType=""
                onBucketActivate={() => {}}
                accountId={accountId}
                handleRiskChange={() => {}}
                currency={currency}
                readOnly={true}
                showAdditionalInfo={false}
                currencyHedgingEnabled={currencyHedgingEnabled}
                rebalancingEnabled={rebalancingEnabled}
                userMode={false}
                overview
              />
              {(store.portfolioChange['LONG_TERM'] || store.data.proposed.buckets[2].oneTimeRebalance) && (
                <>
                  <Typography variant="h3" className={`${classes.proposedSubtitle} LONG_TERM`}>
                    {t('bucket.composition')} {t(`${store.data.proposed.buckets[2].title}.detail`)}
                  </Typography>
                  <PortfolioTableComparison
                    bucketType={store.data.proposed.buckets[2].type}
                    comparison={store.comparison[2]}
                    automaticRebalancing={!!store.data.proposed.buckets[2].automaticRebalancing}
                    oneTimeRebalance={store.data.proposed.buckets[2].oneTimeRebalance}
                    rebalancingTimeTest={store.data.proposed.buckets[2].rebalancingTimeTest}
                    showRebalancing={true}
                    showInstruments={store.portfolioChange['LONG_TERM']}
                  />
                </>
              )}

              {(store.portfolioChange['MID_TERM'] || store.data.proposed.buckets[1].oneTimeRebalance) &&
                store.data.proposed.buckets[1].active && (
                  <>
                    <Typography variant="h3" className={`${classes.proposedSubtitle} MID_TERM`}>
                      {t('bucket.composition')} {t(`${store.data.proposed.buckets[1].title}.detail`)}
                    </Typography>
                    <PortfolioTableComparison
                      bucketType={store.data.proposed.buckets[1].type}
                      comparison={store.comparison[1]}
                      automaticRebalancing={!!store.data.proposed.buckets[1].automaticRebalancing}
                      oneTimeRebalance={store.data.proposed.buckets[1].oneTimeRebalance}
                      rebalancingTimeTest={store.data.proposed.buckets[1].rebalancingTimeTest}
                      showRebalancing={true}
                      showInstruments={store.portfolioChange['MID_TERM']}
                    />
                  </>
                )}

              {(store.portfolioChange['SHORT_TERM'] || store.data.proposed.buckets[0].oneTimeRebalance) &&
                store.data.proposed.buckets[0].active && (
                  <>
                    <Typography variant="h3" className={`${classes.proposedSubtitle} SHORT_TERM`}>
                      {t('bucket.composition')} {t(`${store.data.proposed.buckets[0].title}.detail`)}
                    </Typography>
                    <PortfolioTableComparison
                      bucketType={store.data.proposed.buckets[0].type}
                      comparison={store.comparison[0]}
                      automaticRebalancing={!!store.data.proposed.buckets[0].automaticRebalancing}
                      oneTimeRebalance={store.data.proposed.buckets[0].oneTimeRebalance}
                      rebalancingTimeTest={store.data.proposed.buckets[0].rebalancingTimeTest}
                      showRebalancing={true}
                      showInstruments={store.portfolioChange['SHORT_TERM']}
                    />
                  </>
                )}
            </div>

            <Typography variant="h2" className={classes.originalTitle}>
              {t('accountDetail_overview_originalTitle')}
            </Typography>
            <div className={classes.wrapper}>
              {store.originalChartData.inflation !== undefined && (
                <Chart
                  planData={store.data.original}
                  data={chart(store.data.original, true, store.data.original.externalProductData.externalProducts)}
                  metaData={store.originalChartData}
                  chartTooltipData={chartTooltipData(
                    store.data.original,
                    true,
                    store.data.original.externalProductData.externalProducts,
                  )}
                  currency={currency}
                  stacking="normal"
                  accountId={accountId}
                  store={store}
                  readOnly={true}
                  overview
                  original
                  userMode={false}
                />
              )}
              {store.data.original.externalProductData.externalProducts?.length !== 0 && ( //EDS-4891
                <>
                  <CustomPaper padding={4} style={{ marginTop: '8px', padding: '32px 0' }} inside>
                    <Composition
                      extProdData={store.data.original.externalProductData.externalProducts}
                      composition={store.data.original.chart.composition}
                      finInst={store.data.original.chart.metadata.financialInstitutions}
                      overview
                      original
                    />
                  </CustomPaper>

                  <Typography variant="h3" className={classes.originalSubtitle}>
                    {t('finQuestionnary_goalPlan_resources')}
                  </Typography>
                  <ExternalProducts
                    accountId={accountId}
                    productsData={store.data.original.externalProductData}
                    currency={currency}
                    overview
                    original
                    currentAccount={{
                      title: currentAccountTitle,
                      balance: currentAccountBalance,
                      profit: store.data.original.buckets[2].portfolio.income,
                    }}
                    viewMode={ViewMode.DETAIL}
                  />
                </>
              )}
              <Typography variant="h3" className={classes.originalSubtitle}>
                {t('deposits_plannedDeposits')}
              </Typography>
              <Deposits
                accountId={accountId}
                currency={currency}
                readOnly
                viewMode={ViewMode.DETAIL}
                data={store.data.original.depositData}
                planStore={store}
                handleDepositModal={() => {}}
                userMode={false}
                mode={store.data.original.mode}
                overview
                original
              />

              {store.data.original.reserveData?.reserves.length > 0 && (
                <>
                  <Typography variant="h3" className={classes.originalSubtitle}>
                    {t('accountPlan_reserves')}
                  </Typography>
                  <NewReserves
                    readOnly={true}
                    userMode={false}
                    viewMode={ViewMode.DETAIL}
                    currency={currency}
                    reservesData={store.data.original.reserveData}
                    accountId={accountId}
                    fullWidth={true}
                    collapsed={false}
                    plan={false}
                    planStore={store}
                    overview
                    original
                  />
                </>
              )}

              {(store.data.original.targetData?.targets.length > 0 || store.data.original.rentData) && (
                <>
                  <Typography variant="h3" className={classes.originalSubtitle}>
                    {t('finQuestionnary_target_otherFinancialTargets')}
                  </Typography>
                  <AdditionalTargets
                    accountId={accountId}
                    rentData={store.data.original.rentData}
                    targetsData={store.data.original.targetData.options ? store.data.original.targetData : undefined}
                    targetsOnly={!store.data.original.rentApplicable}
                    plan={false}
                    currency={currency}
                    viewMode={ViewMode.DETAIL}
                    readOnly={true}
                    userMode={false}
                    collapsed={store.data.original.portfolioOnly || store.originalChartData.showRange}
                    personType={store.data.original.personType}
                    planStore={store}
                    defaultInflation={0}
                    defaultInflationType=""
                    overview
                    original
                  />
                </>
              )}
            </div>
          </>
        ) : (
          <div
            style={{
              width: '100%',
              height: '40vh',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
            }}
          >
            <LoadingIndicator size={56} />
            <br />
            {t('accountDetail_overview_loading')}
          </div>
        )}

        <ControlButtons
          isSubmitting={false}
          backText={t(accountDetailStore.prevStepLabel)}
          onBack={() => accountDetailStore.goBack()}
          nextText={t(store.data ? 'general_confirm' : 'general_skip')}
          onNext={() => accountDetailStore.goFwd()}
        >
          <Button
            variant="outlined"
            type="button"
            startIcon={pdfDownloading ? <CircularProgress size={20} /> : <GetAppIcon />}
            onClick={downloadOverview}
            disabled={pdfDownloading}
          >
            {`${t('general_download')} PDF`}
          </Button>
        </ControlButtons>
      </Background>
    </Container>
  );
};

export default React.memo(inject('rootStore', 'apiStore')(observer(Overview)));
